import { CircularProgress, Tooltip } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Help from '@material-ui/icons/Help';
import SettingsIcon from '@material-ui/icons/Settings';
import reloadIcon from 'assets/reload.png';
import teamsIcon from 'assets/teams.png';
import userManualIcon from 'assets/userManual.png';
import { PATHS } from 'constants/paths';
import isEmpty from 'lodash/isEmpty';
import UserRoleSettingsModal from 'pages/WellGuardian/WIR/UserRoleSettingsModal';
import { useUserRoleSettingsModalData } from 'pages/WellGuardian/WIR/UserRoleSettingsModal/hooks';
import { getPhaseName } from 'pages/WellGuardian/WIR/WIRInterventions/components/WIRSchedule/utils';
import WIRField from 'pages/WellGuardian/WIR/components/WIRField';
import { getIsSuperUser } from 'pages/WellGuardian/WIR/utils';
import { NOT_APPLICABLE, StatusTypes } from 'pages/WellGuardian/WIR/wir.constants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { generatePath, useHistory } from 'react-router';
import { Link, useRouteMatch } from 'react-router-dom';
import { useSelectUser } from 'redux/User/selectors';
import {
  updateCountryState,
  updateSelectedAsset,
  updateSelectedSite,
  updateWIRUsers,
} from 'redux/WellGuardian';
import { useCountryState } from 'redux/WellGuardian/selectors';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import {
  AssetType,
  CountryData,
  pendingPhase,
  riskStatus,
  SiteType,
  WIRUser,
} from 'types/wellGuardian';

import styles from './HeaderWellGuardian.module.scss';
import BasicHeader from './components/BasicHeader';
import BasicHeaderWirCreateIntervention from './components/BasicHeaderWirCreateIntervention';
import CountryFlagIcon from './components/CountryFlagIcon/CountryFlagIcon';
import WellGuardianToolbar from './components/WellGuardianToolbar';
import { countryListCheck } from './components/WellGuardianToolbar/utils/countryListCheck';
import {
  getIsInterventionsWirCreation,
  getIsWellHealthSubModule,
} from './components/WellGuardianToolbar/utils/toolbarUtils';
import WellHealthHeader from './components/WellHealthHeader';
import useUpdateWgPageTitleAndFavicon from './hooks/useUpdateWgPageTitleAndFavicon';

const READ_ONLY_WIR_USER = 'READ_ONLY_WIR_USER';
const COUNTRY_CODE = 'countryCode';

const URLData = {
  UserManual: `https://totalworkplace.sharepoint.com/:f:/r/sites/TEPUKDD-WellGuardianUsers418/Documents%20partages/General/Release%20Notes?csf=1&web=1&e=Qw6fmp`,
  TeamsWI_UK: `https://teams.microsoft.com/l/channel/19%3A92f1c90018804c87aa24ee4d2e2eefe4%40thread.tacv2/Digital%20WIR?groupId=c4c4c3ca-e073-4133-8f1f-3358dcd20bf3&tenantId=`,
  TeamsWH_UK: `https://teams.microsoft.com/l/channel/19%3ATUuboYhsRE-3m8hoJKI-cXXaIeFJ8J_iREFrJsJ4Imc1%40thread.tacv2/General?groupId=c4c4c3ca-e073-4133-8f1f-3358dcd20bf3&tenantId=`,
  TeamsWH_DK: `https://teams.microsoft.com/l/channel/19%3Af4fe571fa6f843aba782fe241c0c6996%40thread.tacv2/WG%20-%20Denmark?groupId=c4c4c3ca-e073-4133-8f1f-3358dcd20bf3&tenantId=&ngc=true`,
  TeamsWH_NG: `https://teams.microsoft.com/l/channel/19%3Aecc71f1e6bb54d708cbce8529d6ccc73%40thread.tacv2/WG%20-%20Nigeria?groupId=c4c4c3ca-e073-4133-8f1f-3358dcd20bf3&tenantId=&ngc=true`,
  TeamsWH_AG: `https://teams.microsoft.com/l/channel/19%3A3ff2fbee69cf4ce380eea9451215966d%40thread.tacv2/WG%20-%20Angola?groupId=c4c4c3ca-e073-4133-8f1f-3358dcd20bf3&tenantId=&ngc=true`,
  TeamsWH_CG: `https://teams.microsoft.com/l/channel/19%3Ae7a9a0281d8348d3bd5fa515351b41b2%40thread.tacv2/WG%20-%20Congo?groupId=c4c4c3ca-e073-4133-8f1f-3358dcd20bf3&tenantId=&ngc=true`,
  TeamsWH_BZ: `https://teams.microsoft.com/l/channel/19%3A59bb2778d29347adb33205dc3fd87c94%40thread.tacv2/WG%20-%20Brazil?groupId=c4c4c3ca-e073-4133-8f1f-3358dcd20bf3&tenantId=&ngc=true`,
};

const getWellGuardianHeader = (path: string) => {
  const isWellHealthSubModule = getIsWellHealthSubModule(path);
  const isInterventionsWirCreation = getIsInterventionsWirCreation(path);

  if (isWellHealthSubModule) return <WellHealthHeader />;
  if (isInterventionsWirCreation) return <BasicHeaderWirCreateIntervention />;

  return <BasicHeader />;
};

type HeadCell = {
  id: string;
  label: string | null;
  width?: string;
};

const headCells: HeadCell[] = [
  { id: 'WIR Number', label: 'WIR Number' },
  { id: 'Pending', label: 'Pending Tasks' },
  { id: 'Well', label: 'Well Name' },
  { id: 'Current Phase', label: 'Current Phase' },
  { id: 'status', label: 'Status' },
  { id: 'Intervention Engineer', label: 'Intervention Engineer' },
];

const handleLinkChange = () => PATHS.WELL_GUARDIAN_WELL_HEALTH_HOME;

const HeaderWellGuardian: React.FunctionComponent = () => {
  const intl = useIntl();
  const history = useHistory();
  const match = useRouteMatch();
  const user = useSelectUser();
  const [wirUsers, setWirUsers] = useState<WIRUser[]>([]);
  const { openUserRoleSettingsModal, setOpenUserRoleSettingsModal } =
    useUserRoleSettingsModalData();
  const [count, setCount] = useState<boolean>(false);
  const dispatch = useDispatch();
  const countryState = useCountryState();
  const countryCodeValue = localStorage.getItem(COUNTRY_CODE);
  const isSuperUserOnWirSubModule = getIsSuperUser(
    wirUsers,
    user,
    countryCodeValue ? countryCodeValue : countryState,
  );
  const [pendingStatus, setPendingStatus] = useState<riskStatus[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [countryList, setCountryList] = useState<CountryData[]>([]);
  const [value, setValue] = useState<string>('');

  useUpdateWgPageTitleAndFavicon();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setTotalCount(0);
      if (user && countryState !== '') {
        const res = await wellGuardianApiClient.getPendingTasks(countryState, 'NA');
        const data = res.filter(
          x =>
            x.state !== 'INTERVENTION_COMPLETE' &&
            x.pendingAction.some(
              action => action.userId === user.igg && action.pendingSubtaskCount > 0,
            ),
        );
        setPendingStatus(data);
        setTotalCount(data.length);
      }
      setLoading(false);
    };

    fetchData();
  }, [countryState, user]);

  useEffect(() => {
    const refetchData = async () => {
      setLoading(true);
      if (user && count) {
        const res = await wellGuardianApiClient.getPendingTasks(countryState, 'NA');
        const data = res.filter(
          x =>
            x.state !== 'INTERVENTION_COMPLETE' &&
            x.pendingAction.some(
              action => action.userId === user.igg && action.pendingSubtaskCount > 0,
            ),
        );
        setPendingStatus(data);
        setTotalCount(data.length);
      }
      setLoading(false);
      setCount(false);
    };

    refetchData();
  }, [countryState, user, count]);

  const wirPath = window.location.pathname.split('/')[3];

  const fetchUsersList = useCallback(async () => {
    if (
      wirPath === CountryData.unitedKingdom ||
      wirPath === CountryData.denmark ||
      wirPath === CountryData.nigeria ||
      wirPath === CountryData.angola ||
      wirPath === CountryData.congo ||
      wirPath === CountryData.brazil
    ) {
      const allUsers = await wellGuardianApiClient.wirUsersList(READ_ONLY_WIR_USER, 'NA');
      let selectedCountryList = [];
      selectedCountryList = countryListCheck(allUsers, user);
      setCountryList(selectedCountryList);
      localStorage.setItem(
        COUNTRY_CODE,
        decodeURIComponent(window.location.pathname.split('/')[3]),
      );
      dispatch(updateCountryState(decodeURIComponent(window.location.pathname.split('/')[3])));
      handleLinkChange();
    } else {
      if (user) {
        const allUsers = await wellGuardianApiClient.wirUsersList(READ_ONLY_WIR_USER, 'NA');
        let selectedCountryList = [];
        selectedCountryList = countryListCheck(allUsers, user);
        setCountryList(selectedCountryList);
        if (selectedCountryList[0]) {
          const nextCountry = countryCodeValue ? countryCodeValue : selectedCountryList[0];
          dispatch(updateCountryState(value === '' ? nextCountry : value));
          handleLinkChange();
        } else {
          setCountryList([CountryData.unitedKingdom]);
          dispatch(updateCountryState(CountryData.unitedKingdom));
          handleLinkChange();
        }
        if (allUsers) {
          const finalUsers = allUsers?.filter(x => x.country === countryState);
          dispatch(updateWIRUsers(finalUsers));
        }
        setWirUsers(allUsers);
      }
    }
  }, [countryCodeValue, countryState, dispatch, user, value, wirPath]);

  const userInitials = useMemo(
    () =>
      user && user.firstName && user.lastName
        ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
        : '?',
    [user],
  );

  useEffect(() => {
    fetchUsersList();
  }, [fetchUsersList]);

  function getCountDisplayed(count: number | undefined) {
    if (!count || count <= 0) return 0;

    return count > 99 ? '99+' : `${count}`;
  }

  const handleTIPSelect = (tip?: string, country?: string) => {
    if (tip) {
      history.push(
        generatePath(PATHS.WELL_GUARDIAN_WIR_CREATION, { wirId: tip, country: country }),
      );
    }
  };

  const handleCount = (tip: pendingPhase[]) => {
    const data = tip.filter(x => x.userId === user?.igg);
    const totalPendingSubTaskCount = data.reduce((total, obj) => {
      return total + obj.pendingSubtaskCount;
    }, 0);

    return (
      <span className={styles.gapArea}>
        <span className={styles.countText}>
          {totalPendingSubTaskCount > 9 ? totalPendingSubTaskCount : `0${totalPendingSubTaskCount}`}
        </span>
      </span>
    );
  };

  const getDueDateStatus = (dueDateStatus: string | undefined) => {
    if (dueDateStatus === StatusTypes.ON_TRACK) {
      return (
        <Tooltip title={StatusTypes.ON_TRACK} arrow={true} interactive>
          <div className={styles.status}>
            <div className={styles.statusGreen} />
          </div>
        </Tooltip>
      );
    } else if (dueDateStatus === StatusTypes.BEHIND_SCHEDULE) {
      return (
        <Tooltip title={StatusTypes.BEHIND_SCHEDULE} arrow={true} interactive>
          <div className={styles.status}>
            <div className={styles.statusOrange} />
          </div>
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title={StatusTypes.AT_RISK} arrow={true} interactive>
          <div className={styles.status}>
            <div className={styles.statusRed} />
          </div>
        </Tooltip>
      );
    }
  };

  let toolTipHelp: any;
  if (user) {
    toolTipHelp = (
      <>
        {loading ? (
          <>
            <CircularProgress size={50} color="inherit" />
          </>
        ) : (
          <>
            <div className={styles.helpSettings}>
              <a
                href={`${
                  countryState === CountryData.unitedKingdom
                    ? window.location.pathname === PATHS.WELL_GUARDIAN_INTERVENTIONS
                      ? URLData.TeamsWI_UK
                      : URLData.TeamsWH_UK
                    : countryState === CountryData.denmark
                    ? URLData.TeamsWH_DK
                    : countryState === CountryData.nigeria
                    ? URLData.TeamsWH_NG
                    : countryState === CountryData.angola
                    ? URLData.TeamsWH_AG
                    : countryState === CountryData.congo
                    ? URLData.TeamsWH_CG
                    : countryState === CountryData.brazil
                    ? URLData.TeamsWH_BZ
                    : ''
                }`}
                target="_blank"
                rel="noreferrer noopener"
                className={styles.teams}
              >
                <img src={teamsIcon} alt="" className={styles.teamsIcon} />
                <p className={styles.support}> {`${`WG Help/Support \nTeams Channel`}`}</p>
              </a>
              <a
                href={URLData.UserManual}
                target="_blank"
                rel="noreferrer noopener"
                className={styles.userManual}
              >
                <img src={userManualIcon} alt="" className={styles.groupsIcon} />
                <p className={styles.support}>Release Notes</p>
              </a>
            </div>
          </>
        )}
      </>
    );
  }

  let toolTipLabel: any;

  if (user) {
    toolTipLabel = (
      <>
        {loading ? (
          <>
            <CircularProgress size={50} color="inherit" />
          </>
        ) : (
          <>
            <div className={styles.rowReload}>
              <span className={styles.heading}>
                <span
                  className={styles.subHeading}
                >{`${user?.firstName?.toUpperCase()}  ${user?.lastName?.toUpperCase()}`}</span>
                is having total{' '}
                <span className={styles.subHeading}>{getCountDisplayed(totalCount) || 0}</span>
                pending subTasks
              </span>
              <div className={styles.reload} onClick={() => setCount(true)}>
                <img src={reloadIcon} alt="" />
              </div>
            </div>
            {isEmpty(pendingStatus) ? (
              <span>{NOT_APPLICABLE}</span>
            ) : (
              <TableContainer className={styles.tableContainer}>
                <Table>
                  <TableHead>
                    <TableRow className={styles.tableHeaderRow}>
                      {headCells.map(headCell => (
                        <TableCell
                          width={headCell.width}
                          key={headCell.id}
                          align="left"
                          className={styles.tableHeadCell}
                        >
                          {headCell.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingStatus?.map(tip => (
                      <TableRow key={tip.wirNumber} className={styles.tableBodyRow} hover>
                        <TableCell
                          className={styles.largeTableCell}
                          onClick={e => handleTIPSelect(tip.wirNumber, tip.country)}
                        >
                          <div className={styles.grayTag}>{tip.wirNumber}</div>
                        </TableCell>
                        <TableCell className={styles.tableBodyCell}>
                          {handleCount(tip.pendingAction)}
                        </TableCell>
                        <TableCell className={styles.largeTableBodyCell}>{tip.well}</TableCell>
                        <TableCell className={styles.largeTableBodyCell}>
                          {getPhaseName(intl, tip.state, countryState)}
                        </TableCell>
                        <TableCell className={styles.tableBodyCell}>
                          {getDueDateStatus(tip.dueDateStatus)}
                        </TableCell>
                        <TableCell className={styles.largeTableBodyCell}>
                          {tip.userNameInterventionEngineer ?? NOT_APPLICABLE}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <header className={styles.headerContainer} data-testid="header-well-guardian">
      <Grid container className={styles.pageHeader}>
        {getWellGuardianHeader(match.path)}
        <Grid item container className={styles.rightHeadContainer}>
          <div className={styles.countryField}>
            {countryList.length > 0 ? (
              <WIRField
                id="input-country"
                InputProps={{
                  className: styles.fieldCountryInput,
                }}
                InputLabelProps={{ className: styles.fieldCountryLabel }}
                SelectProps={{
                  className: styles.selectCountryRoot,
                }}
                onChange={event => {
                  setValue(event.target.value);
                  dispatch(updateSelectedSite('ALL' as SiteType));
                  dispatch(updateSelectedAsset('ALL' as AssetType));
                  localStorage.setItem(COUNTRY_CODE, event.target.value);
                }}
                value={countryState}
                select
              >
                {countryList.map(country => (
                  <MenuItem dense value={country} key={country} className={styles.menuLink}>
                    <Link className={styles.linkText} to={handleLinkChange()} key={country}>
                      {CountryFlagIcon(country)}
                      {intl.formatMessage({ id: `wellGuardian.country.${country}` })}
                    </Link>
                  </MenuItem>
                ))}
              </WIRField>
            ) : (
              <CircularProgress size={24} color="inherit" />
            )}
          </div>
          {isSuperUserOnWirSubModule && (
            <Grid item>
              <SettingsIcon
                className={styles.settingsIcon}
                onClick={() => setOpenUserRoleSettingsModal(true)}
              />
            </Grid>
          )}
          <Tooltip
            classes={{ tooltip: styles.tooltipsHelp }}
            title={toolTipHelp}
            arrow={true}
            interactive
          >
            <Grid item>
              <Help className={styles.helpIcon} />
            </Grid>
          </Tooltip>
          <Tooltip
            classes={{ tooltip: styles.tooltipsBG }}
            title={toolTipLabel}
            arrow={true}
            interactive
          >
            <Grid item>
              <div>
                <Avatar classes={{ root: styles.avatar }}>{userInitials}</Avatar>
              </div>
              {totalCount > 0 ? (
                <div className={styles.linkCount}>{getCountDisplayed(totalCount)}</div>
              ) : (
                <div className={styles.linkCount}>{getCountDisplayed(0)}</div>
              )}
            </Grid>
          </Tooltip>
        </Grid>
      </Grid>
      <WellGuardianToolbar path={match.path} />
      <UserRoleSettingsModal
        modalOpen={openUserRoleSettingsModal}
        cancelMethod={() => setOpenUserRoleSettingsModal(false)}
      />
    </header>
  );
};

export default HeaderWellGuardian;
